import Carregando from '../components/Carregando';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, InputAdornment, Snackbar, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import AcessoNegado from '../components/AcessoNegado';
import EditarContratoDialog from '../components/EditarContratoDialog';
import EditarValorContratoDialog from '../components/financeiro/EditarValorContratoDialog';
import BarraSemaforo from '../components/financeiro/BarraSemaforo';
import FormularioNF from '../components/financeiro/FormularioNF';
import ProjetoNFDialog from '../components/financeiro/ProjetoNFDialog';
import RelatoriosDialog from '../components/financeiro/RelatoriosDialog';
import {
  COR, ETIQUETA_ENCERRADO, STATUS, STATUS_NAO_COBRADA, STATUS_ORDEM, abrirNotaFiscal, classificarNotas, extrairErros,
  formatarData, formatarPercentual, formatarReal, resumir, resumirContratos, rotuloCompetencia, situacaoContrato,
  statusDoContratoNovo,
} from '../components/financeiro/financeiroUtils';

const pulso = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55); }
  70% { box-shadow: 0 0 0 12px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

// Colunas da lista de projetos (telas médias e grandes).
const COLUNAS = 'minmax(200px, 2.1fr) minmax(130px, 1.2fr) minmax(110px, 1.1fr) minmax(130px, 1.2fr) minmax(110px, 1.1fr) minmax(120px, 1.2fr) minmax(100px, 1fr) 24px';

const diasTexto = (dias) => `${dias} ${dias === 1 ? 'dia' : 'dias'}`;

// ---------------------------------------------------------------- alerta
function AlertaVencidas({ vencidas, onVer, onRelatorio, onAbrirProjeto }) {
  const total = vencidas.reduce((soma, v) => soma + Number(v.valor || 0), 0);
  const maisAntigas = [...vencidas]
    .sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''))
    .slice(0, 3);

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2.5,
        px: { xs: 2, sm: 3 }, py: 2, borderRadius: 2, color: 'white',
        background: `linear-gradient(100deg, ${STATUS.em_aberto.texto} 0%, #D23C3B 100%)`,
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.18)', animation: `${pulso} 2.4s ease-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <WarningAmberRoundedIcon />
      </Box>

      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>
          {vencidas.length === 1 ? '1 nota fiscal venceu' : `${vencidas.length} notas fiscais venceram`} e ainda não {vencidas.length === 1 ? 'foi recebida' : 'foram recebidas'}
        </Typography>
        <Typography sx={{ fontSize: 14, opacity: 0.92 }}>
          Total vencido: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{formatarReal(total)}</b>
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
          {maisAntigas.map((v) => (
            <Box
              key={v.id} component="button" type="button" onClick={() => onAbrirProjeto(v.chave)}
              sx={{
                border: '1px solid rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.12)', color: 'white', cursor: 'pointer',
                borderRadius: 99, px: 1.5, py: 0.5, fontSize: 12.5, fontFamily: 'inherit', textAlign: 'left',
                '&:hover': { background: 'rgba(255,255,255,0.24)' }, '&:focus-visible': { outline: '2px solid white', outlineOffset: 2 },
              }}
            >
              {v.numero_nf ? `NF ${v.numero_nf}` : 'NF sem número'}, {v.projeto_nome}: {v.dias_para_vencer < 0 ? `${diasTexto(-v.dias_para_vencer)} de atraso` : 'marcada como em aberto'}
            </Box>
          ))}
          {vencidas.length > maisAntigas.length && (
            <Typography sx={{ fontSize: 12.5, alignSelf: 'center', opacity: 0.9 }}>
              e mais {vencidas.length - maisAntigas.length}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained" onClick={onVer}
          sx={{ backgroundColor: 'white', color: STATUS.em_aberto.texto, fontWeight: 700, '&:hover': { backgroundColor: '#fff1f1' } }}
        >
          Ver notas vencidas
        </Button>
        <Button
          variant="outlined" onClick={onRelatorio}
          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.12)' } }}
        >
          Gerar relatório
        </Button>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- painel
function PainelFaturamento({ resumo, contratos, naoCobradas, filtroStatus, onFiltrar, onAbrirProjeto, onVerEncerrados }) {
  const valores = Object.fromEntries(Object.entries(resumo.porStatus).map(([s, v]) => [s, v.valor]));
  return (
    <Box sx={{ backgroundColor: 'white', border: `1px solid ${COR.borda}`, borderRadius: 3, p: { xs: 2, sm: 3 }, mb: 2.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 14, color: COR.texto2 }}>Faturado nos projetos</Typography>
          <Typography sx={{ fontSize: { xs: 30, sm: 38 }, fontWeight: 700, color: COR.ink, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
            {formatarReal(resumo.faturado)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 3, sm: 5 } }}>
          <Box>
            <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Recebido (líquido)</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: COR.navy, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(resumo.recebido)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Faturado quitado</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: COR.navy, fontVariantNumeric: 'tabular-nums' }}>{formatarPercentual(resumo.pctQuitado)}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ----- contratos: o que foi vendido x o que já foi faturado ----- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: { xs: 2, sm: 3 }, mb: 2, pt: 2, borderTop: `1px solid ${COR.borda}` }}>
        <Box>
          <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Total de contratos</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: COR.ink, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(contratos.total)}</Typography>
          <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>
            {contratos.comValor} {contratos.comValor === 1 ? 'contrato com valor informado' : 'contratos com valor informado'}. Projetos cancelados não entram.
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Contratos já faturados</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: COR.navy, fontVariantNumeric: 'tabular-nums' }}>{formatarPercentual(contratos.pctFaturado)}</Typography>
          <Box sx={{ height: 6, borderRadius: 3, backgroundColor: '#e8edf3', my: 0.5, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, contratos.pctFaturado)}%`, backgroundColor: COR.navy, borderRadius: 3 }} />
          </Box>
          <Typography sx={{ fontSize: 12.5, color: COR.texto2, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(contratos.faturado)} faturados</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Saldo a faturar</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: COR.ink, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(contratos.saldo)}</Typography>
          <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>Contrato menos o já faturado</Typography>
        </Box>
      </Box>

      {contratos.estouros.length > 0 && (
        <Box
          role="alert"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
            backgroundColor: STATUS.em_aberto.fundo, border: `1px solid ${STATUS.em_aberto.cor}`,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: STATUS.em_aberto.texto, mt: '1px' }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, color: STATUS.em_aberto.texto }}>
              <strong>{contratos.estouros.length} {contratos.estouros.length === 1 ? 'contrato faturou' : 'contratos faturaram'} mais do que o valor vendido.</strong>
              {' '}Confira se há NF lançada em duplicidade ou se o valor do contrato precisa ser atualizado (aditivo).
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
              {contratos.estouros.map(({ projeto, excesso }) => (
                <Box
                  key={projeto.chave} component="button" type="button" onClick={() => onAbrirProjeto(projeto.chave)}
                  sx={{
                    border: `1px solid ${STATUS.em_aberto.cor}`, background: 'white', color: STATUS.em_aberto.texto, cursor: 'pointer',
                    borderRadius: 99, px: 1.5, py: 0.4, fontSize: 12.5, fontFamily: 'inherit', fontWeight: 600,
                    '&:hover': { background: '#fff1f1' }, '&:focus-visible': { outline: `2px solid ${COR.ink}`, outlineOffset: 2 },
                  }}
                >
                  {projeto.nome}: {formatarReal(excesso)} acima
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {contratos.semValor > 0 && (
        <Box
          role="status"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
            backgroundColor: STATUS.a_receber.fundo, border: `1px solid ${STATUS.a_receber.cor}`,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: STATUS.a_receber.texto, mt: '1px' }} />
          <Typography sx={{ fontSize: 13.5, color: STATUS.a_receber.texto }}>
            <strong>{contratos.semValor} {contratos.semValor === 1 ? 'projeto está' : 'projetos estão'} sem valor de contrato.</strong>
            {' '}Eles não entram nos totais acima. Na lista, clique em “Informar contrato” na linha do projeto.
          </Typography>
        </Box>
      )}

      {resumo.semLiquido.qtd > 0 && (
        <Box
          role="status"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
            backgroundColor: STATUS.a_receber.fundo, border: `1px solid ${STATUS.a_receber.cor}`,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: STATUS.a_receber.texto, mt: '1px' }} />
          <Typography sx={{ fontSize: 13.5, color: STATUS.a_receber.texto }}>
            <strong>{resumo.semLiquido.qtd} {resumo.semLiquido.qtd === 1 ? 'nota recebida está' : 'notas recebidas estão'} sem valor líquido</strong>
            {' '}({formatarReal(resumo.semLiquido.valor)} em NFs). O “Recebido (líquido)” fica incompleto até você informar o valor. Abra o projeto e clique em editar na nota.
          </Typography>
        </Box>
      )}

      {naoCobradas.qtd > 0 && (
        <Box
          role="status"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
            backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: '#475569', mt: '1px' }} />
          <Typography sx={{ fontSize: 13.5, color: '#475569' }}>
            <strong>
              {naoCobradas.qtd} {naoCobradas.qtd === 1 ? 'nota' : 'notas'} ({formatarReal(naoCobradas.valor)}) de projetos cancelados não {naoCobradas.qtd === 1 ? 'entra' : 'entram'} nestes totais
            </strong>
            , porque não {naoCobradas.qtd === 1 ? 'será cobrada' : 'serão cobradas'}.{' '}
            <Box
              component="button" type="button" onClick={onVerEncerrados}
              sx={{ border: 0, background: 'none', p: 0, font: 'inherit', color: COR.navy, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
            >
              Ver em “Encerrados”
            </Box>
          </Typography>
        </Box>
      )}

      <BarraSemaforo valores={valores} altura={22} selecionado={filtroStatus} onSelecionar={onFiltrar} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 2 }}>
        {STATUS_ORDEM.map((s) => {
          const ativo = filtroStatus === s;
          const dados = resumo.porStatus[s];
          return (
            <Tooltip key={s} title={`${STATUS[s].descricao}. Clique para filtrar a lista.`} placement="top">
              <Box
                component="button" type="button" aria-pressed={ativo} onClick={() => onFiltrar(ativo ? null : s)}
                sx={{
                  textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', p: 1.5, borderRadius: 2,
                  border: `1.5px solid ${ativo ? STATUS[s].cor : COR.borda}`,
                  backgroundColor: ativo ? STATUS[s].fundo : 'white',
                  opacity: filtroStatus && !ativo ? 0.55 : 1, transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                  '&:hover': { backgroundColor: STATUS[s].fundo }, '&:focus-visible': { outline: `2px solid ${COR.ink}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: STATUS[s].cor }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: STATUS[s].texto }}>{STATUS[s].label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 19, fontWeight: 700, color: COR.ink, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(dados.valor)}</Typography>
                <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>{dados.qtd} {dados.qtd === 1 ? 'nota' : 'notas'}</Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ------------------------------------------------------- lista de projetos
function LinhaProjeto({ projeto, resumo, naoCobradas, filtroStatus, onAbrir, onEditarContrato }) {
  const vencidas = resumo.porStatus.em_aberto;
  const proximo = resumo.proximoVencimento;
  const abrir = () => onAbrir(projeto.chave);
  const contrato = situacaoContrato(projeto, resumo.faturado);

  return (
    <Box
      role="button" tabIndex={0} onClick={abrir}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(); } }}
      sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: COLUNAS }, columnGap: 2, rowGap: 1.25,
        alignItems: 'center', px: { xs: 2, sm: 2.5 }, py: 1.75, cursor: 'pointer',
        borderTop: `1px solid ${COR.borda}`, outline: 'none',
        backgroundColor: vencidas.qtd ? '#FFF8F8' : 'white',
        boxShadow: vencidas.qtd ? `inset 4px 0 0 ${STATUS.em_aberto.cor}` : 'none',
        transition: 'background-color 120ms ease',
        '&:hover': { backgroundColor: vencidas.qtd ? '#FFF0F0' : COR.fundo },
        '&:focus-visible': { backgroundColor: COR.fundo, boxShadow: `inset 0 0 0 2px ${COR.navy}` },
        '&:hover .seta': { transform: 'translateX(3px)', color: COR.ink },
      }}
    >
      <Box sx={{ gridColumn: { xs: 'span 2', md: 'auto' }, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: COR.ink, lineHeight: 1.35 }}>
          {projeto.nome}
          {ETIQUETA_ENCERRADO[projeto.status] && (
            <Box
              component="span"
              sx={{
                display: 'inline-block', ml: 1, px: 1, py: 0.2, borderRadius: 1.5, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.02em', verticalAlign: '2px', textTransform: 'uppercase',
                backgroundColor: ETIQUETA_ENCERRADO[projeto.status].fundo, color: ETIQUETA_ENCERRADO[projeto.status].texto,
              }}
            >
              {ETIQUETA_ENCERRADO[projeto.status].label}
            </Box>
          )}
        </Typography>
        <Typography sx={{ fontSize: 13, color: COR.texto2 }}>
          {projeto.cliente_nome}
          {projeto.eh_contrato && (
            projeto.projetos.length
              ? ` · Contrato, ${projeto.projetos.length} ${projeto.projetos.length === 1 ? 'projeto' : 'projetos'}`
              : ' · Contrato aguardando projeto'
          )}
        </Typography>
      </Box>

      <Box sx={{ textAlign: { md: 'right' } }}>
        <Typography sx={{ fontSize: 12.5, color: COR.texto2, display: { md: 'none' } }}>Contrato</Typography>
        {contrato.informado ? (
          <>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(contrato.contrato)}</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: contrato.estourou ? 700 : 400, color: contrato.estourou ? STATUS.em_aberto.texto : COR.texto2, fontVariantNumeric: 'tabular-nums' }}>
              {contrato.estourou ? `${formatarReal(-contrato.saldo)} acima do contrato` : `${formatarPercentual(contrato.pctFaturado)} faturado`}
            </Typography>
          </>
        ) : (
          <Box
            component="button" type="button"
            onClick={(e) => { e.stopPropagation(); onEditarContrato(projeto.chave); }}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px', cursor: 'pointer',
              border: `1px solid ${STATUS.a_receber.cor}`, backgroundColor: STATUS.a_receber.fundo, color: STATUS.a_receber.texto,
              fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> Informar contrato
          </Box>
        )}
      </Box>

      <Box sx={{ textAlign: { md: 'right' } }}>
        <Typography sx={{ fontSize: 12.5, color: COR.texto2, display: { md: 'none' } }}>Faturado</Typography>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{resumo.qtd ? formatarReal(resumo.faturado) : '—'}</Typography>
      </Box>

      <Box sx={{ textAlign: { md: 'right' } }}>
        <Typography sx={{ fontSize: 12.5, color: COR.texto2, display: { md: 'none' } }}>Recebido (líquido)</Typography>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: COR.navy, fontVariantNumeric: 'tabular-nums' }}>{resumo.qtd ? formatarReal(resumo.recebido) : '—'}</Typography>
        {resumo.qtd > 0 && (
          <Tooltip title={`${formatarPercentual(resumo.pctQuitado)} do faturado já foi quitado`} placement="bottom">
            <Box sx={{ height: 4, borderRadius: 2, backgroundColor: '#e8edf3', mt: 0.75, ml: { md: 'auto' }, width: { md: 110 }, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${Math.min(100, resumo.pctQuitado)}%`, backgroundColor: COR.navy, borderRadius: 2 }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ textAlign: { md: 'right' } }}>
        <Typography sx={{ fontSize: 12.5, color: COR.texto2, display: { md: 'none' } }}>Em aberto</Typography>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{resumo.qtd ? formatarReal(resumo.aberto) : '—'}</Typography>
        {vencidas.qtd > 0 && (
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: STATUS.em_aberto.texto, fontVariantNumeric: 'tabular-nums' }}>
            {formatarReal(vencidas.valor)} vencido
          </Typography>
        )}
      </Box>

      <Box sx={{ gridColumn: { xs: 'span 1', md: 'auto' } }}>
        <Typography sx={{ fontSize: 12.5, color: COR.texto2, display: { md: 'none' } }}>Próximo vencimento</Typography>
        {proximo ? (
          <>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatarData(proximo.data)}</Typography>
            <Typography sx={{ fontSize: 12.5, color: COR.texto2, fontVariantNumeric: 'tabular-nums' }}>{formatarReal(proximo.valor)}</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
            {vencidas.qtd ? `Venceu em ${formatarData(resumo.maisAntigaVencida)}` : '—'}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, gridColumn: { xs: 'span 1', md: 'auto' } }}>
        {resumo.qtd === 0 && naoCobradas === 0 ? (
          <Typography sx={{ fontSize: 13, color: COR.texto2 }}>Sem lançamentos</Typography>
        ) : STATUS_ORDEM.filter((s) => resumo.porStatus[s].qtd > 0).map((s) => (
          <Tooltip key={s} title={`${resumo.porStatus[s].qtd} ${resumo.porStatus[s].qtd === 1 ? 'nota' : 'notas'}: ${STATUS[s].label.toLowerCase()}`}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.2, borderRadius: 99, fontSize: 12.5, fontWeight: 700,
                backgroundColor: STATUS[s].fundo, color: STATUS[s].texto,
                outline: filtroStatus === s ? `2px solid ${STATUS[s].cor}` : 'none',
              }}
            >
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: STATUS[s].cor }} />
              {resumo.porStatus[s].qtd}
            </Box>
          </Tooltip>
        ))}
        {naoCobradas > 0 && (
          <Tooltip title={`${naoCobradas} ${naoCobradas === 1 ? 'nota' : 'notas'} de projeto cancelado: ${STATUS_NAO_COBRADA.descricao.toLowerCase()}`}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.2, borderRadius: 99, fontSize: 12.5, fontWeight: 700,
                backgroundColor: STATUS_NAO_COBRADA.fundo, color: STATUS_NAO_COBRADA.texto,
              }}
            >
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: STATUS_NAO_COBRADA.cor }} />
              {naoCobradas} {naoCobradas === 1 ? 'não cobrada' : 'não cobradas'}
            </Box>
          </Tooltip>
        )}
      </Box>

      <ChevronRightRoundedIcon className="seta" sx={{ color: '#94a3b8', display: { xs: 'none', md: 'block' }, transition: 'transform 120ms ease, color 120ms ease' }} />
    </Box>
  );
}

// ----------------------------------------------------------------- página
export default function Financeiro() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('financeiro');
  const moduloContratos = true;   // v1.17.0: contratos liberado para todos
  const podeGerenciar = acessoLiberado;

  const [projetos, setProjetos] = useState([]);
  const [contratosNovos, setContratosNovos] = useState(null);   // null = módulo desligado ou ainda não carregou
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [visao, setVisao] = useState('ativos');   // ativos | encerrados | todos
  const [aviso, setAviso] = useState({ tipo: 'success', texto: '' });

  const [itemAbertoChave, setItemAbertoChave] = useState(null);
  const [formulario, setFormulario] = useState({ aberto: false, receita: null });
  const [excluindo, setExcluindo] = useState(null);
  const [relatorio, setRelatorio] = useState({ aberto: false, filtros: null });
  const [contratoEditarChave, setContratoEditarChave] = useState(null);

  const carregar = useCallback(() => {
    if (!acessoLiberado) { setCarregando(false); return; }
    setCarregando(true);
    Promise.allSettled([
      api.get('/projetos/'),
      moduloContratos ? api.get('/contratos/financeiro/') : Promise.resolve(null),
    ]).then(([proj, contr]) => {
      if (proj.status === 'fulfilled') setProjetos(proj.value.data);
      else setAviso({ tipo: 'error', texto: 'Não foi possível carregar os projetos. Atualize a página.' });
      if (contr.status === 'fulfilled') setContratosNovos(contr.value ? contr.value.data : null);
      else {
        setContratosNovos(null);
        setAviso({ tipo: 'error', texto: 'Não foi possível carregar os contratos novos. Atualize a página.' });
      }
    }).finally(() => setCarregando(false));
  }, [acessoLiberado, moduloContratos]);

  useEffect(() => { carregar(); }, [carregar]);

  // ----------------------------------------------------------- itens da lista
  // Cada linha é um projeto antigo ("p5") ou um contrato novo ("c3"). Os ids dos dois se repetem,
  // por isso tudo na tela usa a chave. Projetos de contrato novo não entram na lista: eles aparecem
  // dentro do contrato (só quando os contratos carregaram, para nenhuma NF sumir da tela).
  const itens = useMemo(() => {
    const novos = contratosNovos || [];
    const antigos = projetos
      .filter((p) => !(contratosNovos && p.contrato_novo_id))
      .map((p) => ({
        ...p, chave: `p${p.id}`, eh_contrato: false,
        ...classificarNotas(p.receitas, { itemCancelado: p.status === 'cancelado' }),
      }));
    const contratosComoItens = novos.map((c) => {
      const status = statusDoContratoNovo(c.projetos || []);
      return {
        ...c, chave: `c${c.id}`, eh_contrato: true, status,
        ...classificarNotas(c.receitas || [], {
          itemCancelado: status === 'cancelado',
          projetosCancelados: (c.projetos || []).filter((p) => p.status === 'cancelado').map((p) => p.id),
        }),
      };
    });
    return [...antigos, ...contratosComoItens];
  }, [projetos, contratosNovos]);

  const recarregarItem = async (item) => {
    if (item.eh_contrato) {
      const res = await api.get('/contratos/financeiro/');
      setContratosNovos(res.data);
    } else {
      const res = await api.get(`/projetos/${item.id}/`);
      setProjetos((atual) => atual.map((p) => (p.id === item.id ? res.data : p)));
    }
  };

  // ----------------------------------------------------------- números
  // Só as notas que valem entram nos números. NF não recebida de projeto cancelado não será cobrada.
  const linhas = useMemo(
    () => itens.map((p) => {
      const resumo = resumir(p.receitasValidas);
      const terminou = p.status === 'concluido' || p.status === 'cancelado';
      const aReceber = resumo.qtd - resumo.porStatus.recebido.qtd;
      // Concluído que ainda tem NF a receber continua em "Ativos" até receber tudo.
      return { projeto: p, resumo, naoCobradas: p.naoCobradas.length, encerrado: terminou && aReceber === 0 };
    }),
    [itens],
  );
  const geral = useMemo(() => resumir(itens.flatMap((p) => p.receitasValidas)), [itens]);
  const naoCobradasTotal = useMemo(() => {
    const todas = itens.flatMap((p) => p.naoCobradas);
    return { qtd: todas.length, valor: todas.reduce((soma, r) => soma + Number(r.valor || 0), 0) };
  }, [itens]);
  const contratos = useMemo(() => resumirContratos(itens), [itens]);
  const vencidas = useMemo(
    () => itens.flatMap((p) => p.receitasValidas
      .filter((r) => r.status === 'em_aberto')
      .map((r) => ({ ...r, chave: p.chave, projeto_nome: p.nome }))),
    [itens],
  );
  const contagemVisao = useMemo(() => ({
    ativos: linhas.filter((l) => !l.encerrado).length,
    encerrados: linhas.filter((l) => l.encerrado).length,
    todos: linhas.length,
  }), [linhas]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return linhas
      .filter(({ projeto, resumo, encerrado }) => (
        (!termo || projeto.nome?.toLowerCase().includes(termo) || projeto.cliente_nome?.toLowerCase().includes(termo))
        && (!filtroStatus || resumo.porStatus[filtroStatus].qtd > 0)
        && (visao === 'todos' || (visao === 'encerrados') === encerrado)
      ))
      .sort((a, b) => (
        b.resumo.vencido - a.resumo.vencido
        || (a.resumo.proximoVencimento?.data || '9999').localeCompare(b.resumo.proximoVencimento?.data || '9999')
        || a.projeto.nome.localeCompare(b.projeto.nome, 'pt-BR')
      ));
  }, [linhas, busca, filtroStatus, visao]);

  // ----------------------------------------------------------- ações
  const projetoAberto = itens.find((p) => p.chave === itemAbertoChave) || null;
  const itemParaContrato = itens.find((p) => p.chave === contratoEditarChave) || null;
  const projetoDoContrato = itemParaContrato && !itemParaContrato.eh_contrato ? itemParaContrato : null;
  const contratoNovoEditado = itemParaContrato && itemParaContrato.eh_contrato ? itemParaContrato : null;

  const contratoSalvo = (projetoAtualizado) => {
    setProjetos((atual) => atual.map((p) => (p.id === projetoAtualizado.id ? projetoAtualizado : p)));
    setContratoEditarChave(null);
    setAviso({ tipo: 'success', texto: 'Valor do contrato salvo.' });
  };

  const valorContratoNovoSalvo = async () => {
    const item = contratoNovoEditado;
    setContratoEditarChave(null);
    try {
      await recarregarItem(item);
      setAviso({ tipo: 'success', texto: 'Valor do contrato salvo.' });
    } catch (e) {
      setAviso({ tipo: 'warning', texto: 'Valor salvo, mas não foi possível atualizar a tela. Atualize a página.' });
    }
  };

  const salvarNF = async ({ form, arquivo, remover }) => {
    const dados = new FormData();
    if (projetoAberto.eh_contrato) {
      dados.append('contrato', projetoAberto.id);
      dados.append('projeto', form.projeto ?? '');   // vazio = NF do contrato inteiro
    } else {
      dados.append('projeto', projetoAberto.id);
    }
    ['mes', 'ano', 'numero_nf', 'valor', 'numero_parcela', 'total_parcelas', 'data_vencimento',
      'data_recebimento', 'valor_liquido_recebido', 'status_manual', 'observacao']
      .forEach((campo) => dados.append(campo, form[campo] ?? ''));
    if (arquivo) dados.append('arquivo_nf', arquivo);
    if (remover) dados.append('remover_arquivo_nf', 'true');
    const cabecalhos = { headers: { 'Content-Type': 'multipart/form-data' } };

    try {
      if (formulario.receita) await api.patch(`/receitas/${formulario.receita.id}/`, dados, cabecalhos);
      else await api.post('/receitas/', dados, cabecalhos);
    } catch (err) {
      throw extrairErros(err, 'Não foi possível salvar o lançamento.');
    }
    setFormulario({ aberto: false, receita: null });
    try {
      await recarregarItem(projetoAberto);
      setAviso({ tipo: 'success', texto: 'Lançamento salvo.' });
    } catch (e) {
      setAviso({ tipo: 'warning', texto: 'Lançamento salvo, mas não foi possível atualizar a tela. Atualize a página.' });
    }
  };

  const confirmarExclusao = async () => {
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await api.delete(`/receitas/${alvo.id}/`);
    } catch (err) {
      setAviso({ tipo: 'error', texto: extrairErros(err, 'Não foi possível excluir o lançamento.').mensagem });
      return;
    }
    try {
      await recarregarItem(projetoAberto);
      setAviso({ tipo: 'success', texto: 'Lançamento excluído.' });
    } catch (err) {
      setAviso({ tipo: 'warning', texto: 'Lançamento excluído, mas não foi possível atualizar a tela. Atualize a página.' });
    }
  };

  const abrirNF = (receita) => abrirNotaFiscal(receita).catch((e) => setAviso({ tipo: 'error', texto: e.message }));

  const abrirRelatorio = (filtros = null) => setRelatorio({ aberto: true, filtros });

  const verVencidas = () => {
    setFiltroStatus('em_aberto');
    setVisao('ativos');
    setBusca('');
    document.getElementById('lista-projetos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!acessoLiberado) {
    return <AcessoNegado mensagem="Você não tem acesso ao Financeiro. Fale com o Admin se precisar desse acesso." />;
  }

  return (
    <Box sx={{ '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } }}>
      <PageHeader titulo="Financeiro" />

      {vencidas.length > 0 && (
        <AlertaVencidas
          vencidas={vencidas}
          onVer={verVencidas}
          onRelatorio={() => abrirRelatorio({ status: ['em_aberto'] })}
          onAbrirProjeto={setItemAbertoChave}
        />
      )}

      {carregando ? (
        <Carregando />
      ) : (
        <>
          <PainelFaturamento resumo={geral} contratos={contratos} naoCobradas={naoCobradasTotal} filtroStatus={filtroStatus} onFiltrar={setFiltroStatus} onAbrirProjeto={setItemAbertoChave} onVerEncerrados={() => setVisao('encerrados')} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <TextField
              placeholder="Buscar por projeto, contrato ou cliente" value={busca} onChange={(e) => setBusca(e.target.value)}
              size="small" sx={{ flex: '1 1 260px', backgroundColor: 'white' }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> } }}
            />
            <ToggleButtonGroup
              exclusive size="small" value={visao} aria-label="Quais projetos mostrar"
              onChange={(e, novo) => { if (novo) setVisao(novo); }}
              sx={{
                backgroundColor: 'white',
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2, color: '#475569', borderColor: '#cbd5e1' },
                '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': { backgroundColor: COR.ink, color: 'white' },
              }}
            >
              <ToggleButton value="ativos">Ativos ({contagemVisao.ativos})</ToggleButton>
              <ToggleButton value="encerrados">Encerrados ({contagemVisao.encerrados})</ToggleButton>
              <ToggleButton value="todos">Todos ({contagemVisao.todos})</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained" startIcon={<AssessmentOutlinedIcon />} onClick={() => abrirRelatorio(null)}
              sx={{ backgroundColor: COR.destaque, fontWeight: 700, px: 2.5, '&:hover': { backgroundColor: '#d23a54' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Relatórios de faturamento e recebimento</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Relatórios</Box>
            </Button>
          </Box>

          {filtroStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
                Mostrando projetos e contratos com notas <b style={{ color: STATUS[filtroStatus].texto }}>{STATUS[filtroStatus].label.toLowerCase()}</b>.
              </Typography>
              <Button size="small" onClick={() => setFiltroStatus(null)}>Limpar filtro</Button>
            </Box>
          )}

          <Box id="lista-projetos" sx={{ backgroundColor: 'white', border: `1px solid ${COR.borda}`, borderRadius: 3, overflow: 'hidden', scrollMarginTop: 16 }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' }, gridTemplateColumns: COLUNAS, columnGap: 2, px: 2.5, py: 1.25,
                backgroundColor: COR.fundo, fontSize: 12.5, fontWeight: 600, color: COR.texto2,
              }}
            >
              <span>Projeto</span>
              <span style={{ textAlign: 'right' }}>Contrato</span>
              <span style={{ textAlign: 'right' }}>Faturado</span>
              <span style={{ textAlign: 'right' }}>Recebido (líquido)</span>
              <span style={{ textAlign: 'right' }}>Em aberto</span>
              <span>Próximo vencimento</span>
              <span>Situação das notas</span>
              <span />
            </Box>

            {visiveis.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, borderTop: `1px solid ${COR.borda}` }}>
                <Typography sx={{ fontWeight: 700, color: COR.ink }}>Nenhum projeto ou contrato encontrado</Typography>
                <Typography sx={{ fontSize: 14, color: COR.texto2 }}>Ajuste a busca, limpe o filtro de situação ou escolha “Todos”.</Typography>
              </Box>
            ) : (
              visiveis.map(({ projeto, resumo, naoCobradas }) => (
                <LinhaProjeto key={projeto.chave} projeto={projeto} resumo={resumo} naoCobradas={naoCobradas} filtroStatus={filtroStatus} onAbrir={setItemAbertoChave} onEditarContrato={setContratoEditarChave} />
              ))
            )}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: COR.texto2, mt: 1 }}>
            {visiveis.length} {visiveis.length === 1 ? 'item' : 'itens'} (projetos e contratos). Os com notas vencidas aparecem primeiro. A barra de progresso mostra quanto do faturado já foi quitado. Projeto concluído que ainda tem NF a receber fica em “Ativos” até receber tudo.
          </Typography>
        </>
      )}

      <ProjetoNFDialog
        projeto={projetoAberto}
        podeGerenciar={podeGerenciar}
        onFechar={() => setItemAbertoChave(null)}
        onNova={() => setFormulario({ aberto: true, receita: null })}
        onEditar={(receita) => setFormulario({ aberto: true, receita })}
        onExcluir={setExcluindo}
        onAbrirNF={abrirNF}
        onEditarContrato={(p) => setContratoEditarChave(p.chave)}
      />

      <EditarContratoDialog projeto={projetoDoContrato} onFechar={() => setContratoEditarChave(null)} onSalvo={contratoSalvo} />
      <EditarValorContratoDialog contrato={contratoNovoEditado} onFechar={() => setContratoEditarChave(null)} onSalvo={valorContratoNovoSalvo} />

      <FormularioNF
        aberto={formulario.aberto}
        receita={formulario.receita}
        projeto={projetoAberto}
        onFechar={() => setFormulario({ aberto: false, receita: null })}
        onSalvar={salvarNF}
      />

      <RelatoriosDialog
        aberto={relatorio.aberto}
        filtrosIniciais={relatorio.filtros}
        projetos={itens}
        onFechar={() => setRelatorio((r) => ({ ...r, aberto: false }))}
      />

      <Dialog open={Boolean(excluindo)} onClose={() => setExcluindo(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } } } }}>
        <DialogTitle>Excluir este lançamento?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {excluindo && `${excluindo.numero_nf ? `NF ${excluindo.numero_nf}` : 'O lançamento'} de ${rotuloCompetencia(excluindo.mes, excluindo.ano)}, no valor de ${formatarReal(excluindo.valor)}, será removido junto com a NF anexada. Esta ação não pode ser desfeita.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExcluindo(null)} sx={{ color: COR.texto2 }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarExclusao}>Excluir lançamento</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(aviso.texto)} autoHideDuration={4500} onClose={() => setAviso((a) => ({ ...a, texto: '' }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={aviso.tipo} variant="filled" onClose={() => setAviso((a) => ({ ...a, texto: '' }))}>{aviso.texto}</Alert>
      </Snackbar>
    </Box>
  );
}
